import { sessionManager, httpClient } from '../core/SessionManager';
import { fetchWithAuth } from '../store/fetchClient';
import {
  getPushEnvironment,
  getPushSupportMessage,
  shouldRegisterBrowserServiceWorker,
  type PushEnvironment,
} from '../utils/pushEnvironment';

export interface NotificationSettings {
  id: string;
  globalEnabled: boolean;
  preferences: {
    tasks: {
      new_assignment: boolean;
      due_today: boolean;
      overdue: boolean;
      completed: boolean;
    };
    cleaning: {
      new_task: boolean;
      due_today: boolean;
      overdue: boolean;
      approval_needed: boolean;
      approved: boolean;
    };
    system: {
      maintenance: boolean;
      updates: boolean;
      emergency: boolean;
    };
    schedule: {
      start_time: string;
      end_time: string;
      weekend_enabled: boolean;
      daily_summary: boolean;
      daily_summary_time: string;
    };
  };
}

export interface PushSubscriptionInfo {
  id: string;
  device_type: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  last_used_at: string;
  created_at: string;
}

export interface NotificationHistoryItem {
  id: string;
  notification_type: string;
  payload: {
    title: string;
    body: string;
    data?: unknown;
  };
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'emergency';
  scheduled_for: string;
  sent_at?: string;
  created_at: string;
}

export interface NotificationHistoryResponse {
  notifications: NotificationHistoryItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

type SubscriptionRegistrationPayload = {
  subscription: PushSubscriptionJSON;
  client: {
    pushMode: PushEnvironment['pushMode'];
    isStandalone: boolean;
    isNativeShell: boolean;
    userAgent: string;
  };
};

export class NotificationService {
  private static vapidPublicKey: string | null = null;
  private static registration: ServiceWorkerRegistration | null = null;
  private static subscription: PushSubscription | null = null;
  private static initializePromise: Promise<boolean> | null = null;

  public static isSupported(): boolean {
    return this.getPushEnvironment().canUsePush && !this.isCurrentSessionBlocked();
  }

  public static getPermissionStatus(): NotificationPermission {
    if (typeof Notification === 'undefined') {
      return 'default';
    }

    return Notification.permission;
  }

  public static async initialize(): Promise<boolean> {
    if (this.initializePromise) {
      return this.initializePromise;
    }

    this.initializePromise = this.initializeInternal().finally(() => {
      this.initializePromise = null;
    });

    return this.initializePromise;
  }

  public static async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      console.info(getPushSupportMessage(this.getPushEnvironment()));
      return false;
    }

    if (typeof Notification === 'undefined') {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  public static async subscribe(): Promise<boolean> {
    if (!this.isSupported()) {
      console.info(getPushSupportMessage(this.getPushEnvironment()));
      return false;
    }

    if (!this.isAuthenticatedBrowserSession()) {
      console.warn('Push-Subscription erfordert eine angemeldete Browser-Sitzung.');
      return false;
    }

    if (this.getPermissionStatus() !== 'granted') {
      const granted = await this.requestPermission();
      if (!granted) {
        return false;
      }
    }

    const ready = await this.initialize();
    if (!ready || !this.registration || !this.vapidPublicKey) {
      console.warn('Push-Komponenten konnten nicht initialisiert werden.');
      return false;
    }

    try {
      let subscription = await this.registration.pushManager.getSubscription();

      if (!subscription) {
        subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey) as BufferSource,
        });
      }

      const synced = await this.registerSubscription(subscription);
      if (!synced) {
        return false;
      }

      this.subscription = subscription;
      return true;
    } catch (error) {
      console.error('Fehler beim Einrichten der Push-Subscription:', error);
      return false;
    }
  }

  public static async unsubscribe(): Promise<boolean> {
    try {
      const subscription = await this.resolveCurrentSubscription();
      if (!subscription) {
        return true;
      }

      try {
        await fetchWithAuth('/api/notifications/unsubscribe', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
      } catch (error) {
        console.warn('Serverseitiges Unsubscribe fehlgeschlagen, lokale Subscription wird trotzdem entfernt.', error);
      }

      await subscription.unsubscribe();
      this.subscription = null;
      return true;
    } catch (error) {
      console.error('Fehler beim Entfernen der Push-Subscription:', error);
      return false;
    }
  }

  public static async isSubscribed(): Promise<boolean> {
    try {
      const subscription = await this.resolveCurrentSubscription();
      return Boolean(subscription);
    } catch (error) {
      console.error('Fehler beim Pruefen des Subscription-Status:', error);
      return false;
    }
  }

  public static async getSettings(): Promise<NotificationSettings | null> {
    try {
      const response = await fetchWithAuth('/api/notifications/settings');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Fehler beim Laden der Benachrichtigungs-Einstellungen:', error);
      return null;
    }
  }

  public static async updateSettings(settings: Partial<NotificationSettings>): Promise<boolean> {
    try {
      const response = await fetchWithAuth('/api/notifications/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });

      return response.ok;
    } catch (error) {
      console.error('Fehler beim Speichern der Benachrichtigungs-Einstellungen:', error);
      return false;
    }
  }

  public static async getSubscriptions(): Promise<PushSubscriptionInfo[]> {
    try {
      const response = await fetchWithAuth('/api/notifications/subscriptions');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Fehler beim Laden der Push-Subscriptions:', error);
      return [];
    }
  }

  public static async removeSubscription(subscriptionId: string): Promise<boolean> {
    try {
      await httpClient.delete(`/api/notifications/subscriptions/${subscriptionId}`);
      return true;
    } catch (error) {
      console.error('Fehler beim Loeschen der Push-Subscription:', error);
      return false;
    }
  }

  public static async sendTestNotification(message?: string): Promise<boolean> {
    if (!this.isSupported()) {
      console.info(getPushSupportMessage(this.getPushEnvironment()));
      return false;
    }

    try {
      const environment = this.getPushEnvironment();
      const response = await fetchWithAuth('/api/notifications/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message || 'Dies ist eine Test-Benachrichtigung.',
          platform: {
            os: environment.isIOS ? 'ios' : environment.isAndroid ? 'android' : 'unknown',
            browser: environment.pushMode,
            timestamp: Date.now(),
          },
        }),
      });

      return response.ok;
    } catch (error) {
      console.error('Fehler beim Senden der Test-Benachrichtigung:', error);
      return false;
    }
  }

  public static async getHistory(
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<NotificationHistoryResponse | null> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (status) {
        params.set('status', status);
      }

      const response = await fetchWithAuth(`/api/notifications/history?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Fehler beim Laden der Benachrichtigungs-Historie:', error);
      return null;
    }
  }

  public static async sendLocalTestNotification(message?: string): Promise<boolean> {
    if (this.getPermissionStatus() !== 'granted') {
      return false;
    }

    try {
      const registration = await this.ensureServiceWorkerRegistration();
      const title = 'Push-Test';
      const options: NotificationOptions = {
        body: message || 'Lokale Test-Benachrichtigung',
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-192x192.png',
        tag: 'push-local-test',
        data: { source: 'local-test', timestamp: Date.now() },
      };

      if (registration) {
        await registration.showNotification(title, options);
      } else {
        const notification = new Notification(title, options);
        window.setTimeout(() => notification.close(), 4000);
      }

      return true;
    } catch (error) {
      console.error('Fehler bei der lokalen Test-Benachrichtigung:', error);
      return false;
    }
  }

  public static async performSystemDiagnosis(): Promise<Record<string, unknown>> {
    const environment = this.getPushEnvironment();
    const subscription = await this.resolveCurrentSubscription().catch(() => null);

    return {
      timestamp: new Date().toISOString(),
      environment,
      serviceWorkerRegistered: Boolean(this.registration),
      vapidKeyLoaded: Boolean(this.vapidPublicKey),
      permission: this.getPermissionStatus(),
      subscribed: Boolean(subscription),
      supportMessage: getPushSupportMessage(environment),
    };
  }

  public static getServiceStatus(): {
    available: boolean;
    features: string[];
    limitations: string[];
  } {
    const environment = this.getPushEnvironment();
    const features: string[] = [];
    const limitations: string[] = [];

    if (environment.supportsNotifications) {
      features.push('Notification API');
    } else {
      limitations.push('Notification API fehlt');
    }

    if (environment.supportsServiceWorker) {
      features.push('Service Worker');
    } else {
      limitations.push('Service Worker fehlt');
    }

    if (environment.supportsPushManager) {
      features.push('Push API');
    } else {
      limitations.push('Push API fehlt');
    }

    if (!environment.canUsePush) {
      limitations.push(getPushSupportMessage(environment));
    }

    return {
      available: environment.canUsePush,
      features,
      limitations,
    };
  }

  private static async initializeInternal(): Promise<boolean> {
    if (!shouldRegisterBrowserServiceWorker()) {
      return false;
    }

    if (!this.isSupported()) {
      return false;
    }

    try {
      await this.ensureServiceWorkerRegistration();
      await this.loadVapidKey();
      await this.resolveCurrentSubscription();
      return Boolean(this.registration && this.vapidPublicKey);
    } catch (error) {
      console.error('Fehler bei der Initialisierung des Push-Services:', error);
      return false;
    }
  }

  private static async ensureServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (this.registration) {
      return this.registration;
    }

    if (!shouldRegisterBrowserServiceWorker() || !('serviceWorker' in navigator)) {
      return null;
    }

    const existingRegistration = await navigator.serviceWorker.getRegistration('/');
    if (existingRegistration?.active?.scriptURL.endsWith('/service-worker.js')) {
      this.registration = existingRegistration;
      return this.registration;
    }

    this.registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      updateViaCache: 'none',
    });

    await navigator.serviceWorker.ready;
    return this.registration;
  }

  private static async loadVapidKey(): Promise<void> {
    if (this.vapidPublicKey) {
      return;
    }

    const response = await fetchWithAuth('/api/notifications/vapid-public-key');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    this.vapidPublicKey = await response.text();
  }

  private static async resolveCurrentSubscription(): Promise<PushSubscription | null> {
    const registration = await this.ensureServiceWorkerRegistration();
    if (!registration) {
      this.subscription = null;
      return null;
    }

    this.subscription = await registration.pushManager.getSubscription();
    return this.subscription;
  }

  private static async registerSubscription(subscription: PushSubscription): Promise<boolean> {
    const environment = this.getPushEnvironment();
    const payload: SubscriptionRegistrationPayload = {
      subscription: subscription.toJSON(),
      client: {
        pushMode: environment.pushMode,
        isStandalone: environment.isStandalone,
        isNativeShell: environment.isNativeShell,
        userAgent: navigator.userAgent,
      },
    };

    const response = await fetchWithAuth('/api/notifications/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    return response.ok;
  }

  private static getPushEnvironment(): PushEnvironment {
    return getPushEnvironment();
  }

  private static isCurrentSessionBlocked(): boolean {
    const snapshot = sessionManager.getSnapshot();
    return snapshot.user?.role === 'tablet' || snapshot.user?.sessionKind === 'native_dev_sim';
  }

  private static isAuthenticatedBrowserSession(): boolean {
    const snapshot = sessionManager.getSnapshot();
    return snapshot.status === 'authenticated' && Boolean(snapshot.user) && !this.isCurrentSessionBlocked();
  }

  private static urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const normalizedBase64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(normalizedBase64);
    const outputArray = new Uint8Array(rawData.length);

    for (let index = 0; index < rawData.length; index += 1) {
      outputArray[index] = rawData.charCodeAt(index);
    }

    return outputArray;
  }
}
